export const ARCGIS_ACCOUNT_STORAGE_KEY = 'velocity-migration-assistant:arcgis-account-manager';
export const ARCGIS_PENDING_OAUTH_STORAGE_KEY = 'velocity-migration-assistant:arcgis-oauth-request';
export const ARCGIS_AUTH_BROADCAST_CHANNEL = 'velocity-migration-assistant:arcgis-auth';

export type ArcgisAuthProvider = 'arcgis-online' | 'arcgis-enterprise';

export interface StoredArcgisAccount {
  id: string;
  provider: ArcgisAuthProvider;
  portalUrl: string;
  portalName?: string;
  clientId: string;
  username: string;
  fullName?: string;
  thumbnailDataUrl?: string;
  orgId?: string;
  orgName?: string;
  serializedSession: string;
  tokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  updatedAt: string;
}

export interface StoredArcgisAccountState {
  version: 1;
  activeAccountId?: string;
  accounts: StoredArcgisAccount[];
}

export interface PendingOAuthRequest {
  provider: ArcgisAuthProvider;
  portalUrl: string;
  clientId: string;
  redirectUri: string;
  expirationMinutes: number;
  startedAt: string;
}

export interface BrowserStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function createAccountId(provider: ArcgisAuthProvider, portalUrl: string, username: string): string {
  return [provider, portalUrl.toLowerCase(), username.toLowerCase()].map(encodeURIComponent).join(':');
}

export function loadAccountState(storage = getDefaultStorage()): StoredArcgisAccountState {
  if (!storage) return createEmptyAccountState();

  const rawValue = storage.getItem(ARCGIS_ACCOUNT_STORAGE_KEY);
  if (!rawValue) return createEmptyAccountState();

  try {
    const parsedValue = JSON.parse(rawValue) as StoredArcgisAccountState;
    if (parsedValue.version !== 1 || !Array.isArray(parsedValue.accounts)) {
      return createEmptyAccountState();
    }
    return {
      version: 1,
      activeAccountId: typeof parsedValue.activeAccountId === 'string' ? parsedValue.activeAccountId : undefined,
      accounts: parsedValue.accounts.filter(isStoredArcgisAccount),
    };
  } catch {
    return createEmptyAccountState();
  }
}

export function loadActiveAccount(storage = getDefaultStorage()): StoredArcgisAccount | undefined {
  const state = loadAccountState(storage);
  return state.accounts.find((account) => account.id === state.activeAccountId);
}

export function saveActiveAccount(account: StoredArcgisAccount, storage = getDefaultStorage()): void {
  if (!storage) return;

  const state = loadAccountState(storage);
  const accounts = [account, ...state.accounts.filter((storedAccount) => storedAccount.id !== account.id)];
  storage.setItem(
    ARCGIS_ACCOUNT_STORAGE_KEY,
    JSON.stringify({
      version: 1,
      activeAccountId: account.id,
      accounts,
    } satisfies StoredArcgisAccountState),
  );
}

export function removeStoredAccount(accountId: string, storage = getDefaultStorage()): void {
  if (!storage) return;

  const state = loadAccountState(storage);
  const accounts = state.accounts.filter((account) => account.id !== accountId);
  const activeAccountId = state.activeAccountId === accountId ? accounts[0]?.id : state.activeAccountId;
  storage.setItem(
    ARCGIS_ACCOUNT_STORAGE_KEY,
    JSON.stringify({
      version: 1,
      activeAccountId,
      accounts,
    } satisfies StoredArcgisAccountState),
  );
}

export function clearStoredAccounts(storage = getDefaultStorage()): void {
  storage?.removeItem(ARCGIS_ACCOUNT_STORAGE_KEY);
}

export function savePendingOAuthRequest(request: PendingOAuthRequest, storage = getDefaultStorage()): void {
  storage?.setItem(ARCGIS_PENDING_OAUTH_STORAGE_KEY, JSON.stringify(request));
}

export function loadPendingOAuthRequest(storage = getDefaultStorage()): PendingOAuthRequest | undefined {
  if (!storage) return undefined;

  const rawValue = storage.getItem(ARCGIS_PENDING_OAUTH_STORAGE_KEY);
  if (!rawValue) return undefined;

  try {
    const parsedValue = JSON.parse(rawValue) as PendingOAuthRequest;
    if (
      isAuthProvider(parsedValue.provider) &&
      typeof parsedValue.portalUrl === 'string' &&
      typeof parsedValue.clientId === 'string' &&
      typeof parsedValue.redirectUri === 'string' &&
      typeof parsedValue.expirationMinutes === 'number'
    ) {
      return parsedValue;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

export function clearPendingOAuthRequest(storage = getDefaultStorage()): void {
  storage?.removeItem(ARCGIS_PENDING_OAUTH_STORAGE_KEY);
}

export function isStoredAccountExpired(account: StoredArcgisAccount, now = Date.now()): boolean {
  if (account.refreshTokenExpiresAt) {
    return Date.parse(account.refreshTokenExpiresAt) <= now;
  }
  if (account.tokenExpiresAt) {
    return Date.parse(account.tokenExpiresAt) <= now;
  }
  return false;
}

function createEmptyAccountState(): StoredArcgisAccountState {
  return { version: 1, accounts: [] };
}

function getDefaultStorage(): BrowserStorage | undefined {
  return globalThis.localStorage;
}

function isStoredArcgisAccount(value: unknown): value is StoredArcgisAccount {
  const account = value as StoredArcgisAccount;
  return (
    typeof account?.id === 'string' &&
    isAuthProvider(account.provider) &&
    typeof account.portalUrl === 'string' &&
    typeof account.clientId === 'string' &&
    typeof account.username === 'string' &&
    typeof account.serializedSession === 'string' &&
    typeof account.updatedAt === 'string'
  );
}

function isAuthProvider(value: unknown): value is ArcgisAuthProvider {
  return value === 'arcgis-online' || value === 'arcgis-enterprise';
}
